<template>
  <div class="chart-wrapper print-chart-wrapper mt-15">
    <LoadingAnimation v-if="loading" />
    <div class="chart-content" v-else>
      <div class="hour-title">
        <span class="hour-item pt-2 pb-2" :class="{ active: timeIconActive }">
          <i class="el-icon-time bold-font font-18"></i>
        </span>
        <span
          v-for="hour in hours"
          :key="hour.value"
          class="hour-item pt-2 pb-2"
          :class="hour.active ? 'active' : ''"
          >{{ hour.show ? hour.label : '' }}
        </span>
      </div>
      <div v-for="(day, index) in chartData" :key="day.Date" class="day-item">
        <div class="day-title">
          <div>Day {{ index + 1 }}</div>
          <div>
            {{
              userLang.indexOf('en') > -1
                ? moment(day.Date).format('DD MMM YY')
                : moment(day.Date).format('DD/MM/YY')
            }}
          </div>
        </div>
        <div class="day-icon-content" v-if="day.Data.length">
          <!-- pad cycle -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday"
            class="day-icon-item"
            :style="{
              left: (dayItem.StartToToday / dayDuration) * 100 + '%',
              width: (dayItem.BlockWidth / dayDuration) * 100 + '%'
            }"
          >
            <div
              class="pad-cycle-item"
              :class="{
                'pad-start': dayItem.IsStarted === padCycleStartType.isStart,
                'pad-end':
                  dayItem.IsCompleted ===
                    padCycleEndType.isCompletedImmediate ||
                  dayItem.IsCompleted === padCycleEndType.isCompleted
              }"
            ></div>
          </div>
          <!-- void event in pad -->
          <div
            v-for="dayItem in day.Data"
            :key="dayItem.PadCycleId + dayItem.StartToToday + 'event'"
          >
            <div
              v-for="(item, index) in dayItem.Voids"
              :key="item.TimeStamp + item.Status"
            >
              <!-- duration -->
              <el-tooltip
                v-if="item.Duration"
                placement="top"
                effect="light"
                popper-class="event-detail-popup"
              >
                <!-- poper content -->
                <div slot="content">
                  <div class="event-item">
                    <div
                      class="smart-event-icon left-side"
                      :class="getEventInfo(item).class"
                    >
                      <img
                        v-if="getEventInfo(item).imgSrc"
                        :src="getEventInfo(item).imgSrc"
                      />
                    </div>
                    <div class="inline-block">
                      <div class="status-text secondary-font">
                        {{ getEventInfo(item).text | firstLetterCapitalize }}
                      </div>
                      <div>
                        {{ moment(item.TimeStamp).format('h:mm A') }}
                      </div>
                      <div>
                        <div class="status-text mt-5 secondary-font">
                          {{ $t('text.duration') }}
                        </div>
                        <div>{{ item.Duration | secondFormat }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  :style="{
                    left: (item.StartToToday / dayDuration) * 100 + '%',
                    width: (item.Duration / dayDuration) * 100 + '%'
                  }"
                  class="void-event-with-duration"
                >
                  <div
                    v-if="item.Status === voidConst.outOfRange"
                    class="lost-pod-connection-item"
                  ></div>
                  <!-- WETSURFACE, UNSTABLE, CLUMPED is a group, so these three states show the same icon -->
                  <!-- <div v-else class="external-humidity">
                    <img
                      class="start-external-icon"
                      src="@/assets/image/VoidingPatternIcon/external-humidity.png"
                    />
                    <img
                      class="end-external-icon"
                      src="@/assets/image/VoidingPatternIcon/external-humidity.png"
                    />
                  </div> -->
                </div>
              </el-tooltip>
              <!-- icon -->
              <el-tooltip
                v-else
                placement="top"
                effect="light"
                popper-class="event-detail-popup"
              >
                <!-- poper content -->
                <div slot="content">
                  <Tooltip
                    :eventInfo="getEventInfo(item)"
                    :item="item"
                    :dayItem="dayItem"
                  />
                  <Tooltip
                    v-for="subEvent in item.SubEvents"
                    :key="subEvent.TimeStamp + subEvent.Status"
                    :eventInfo="getEventInfo(subEvent)"
                    :item="subEvent"
                    :dayItem="dayItem"
                  />
                </div>
                <!-- There are multiple events near this icon, which are displayed in combination -->
                <!-- only show the top icon and the bottom icon's edge in the chart -->
                <div
                  v-if="item.SubEvents.length"
                  class="stack-icon-wrapper-in-tooltip"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                >
                  <!-- bottom icon's edge -->
                  <div
                    class="event-icon"
                    :class="
                      getEventInfo(getBottomAndTopIcon(item).bottom).class
                    "
                  ></div>
                  <!-- top icon -->
                  <div
                    class="event-icon top-icon"
                    :class="getEventInfo(getBottomAndTopIcon(item).top).class"
                  >
                    <img
                      v-if="getEventInfo(getBottomAndTopIcon(item).top).imgSrc"
                      :src="getEventInfo(getBottomAndTopIcon(item).top).imgSrc"
                    />
                  </div>
                </div>
                <!-- Single event -->
                <div
                  v-else
                  class="event-icon"
                  :style="{
                    left: getEventPosition(dayItem, index)
                  }"
                  :class="getEventInfo(item).class"
                >
                  <img
                    v-if="getEventInfo(item).imgSrc"
                    :src="getEventInfo(item).imgSrc"
                  />
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { images, voidState, hours } from '@/const.js'
import LoadingAnimation from '@/components/patients/charts/LoadingAnimation'
import Tooltip from '@/components/patients/charts/voidingChart/Tooltip'
import {
  padCycleEndType,
  padCycleStartType,
  voidConst
} from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
export default {
  data() {
    return {
      images,
      voidState,
      moment,
      userLang: navigator.language || navigator.userLanguage,
      hours,
      dayDuration: 86400,
      padCycleEndType,
      padCycleStartType,
      voidConst
    }
  },
  props: {
    chartData: {
      type: Array,
      require: true
    },
    loading: {
      type: Boolean,
      require: true
    },
    timeIconActive: {
      type: Boolean,
      default: false
    }
  },
  components: {
    LoadingAnimation,
    Tooltip
  },
  computed: {
    getEventPosition() {
      return (dayItem, index) => {
        let currentEvent = dayItem.Voids[index]
        let left = (currentEvent.StartToToday / this.dayDuration) * 100 + '%'
        // If it is an icon at the end of the pad cycle, move it to the right and do not exceed the pad cycle
        if (
          moment(dayItem.EndDate).diff(
            moment(currentEvent.TimeStamp),
            'minutes'
          ) < 20
        ) {
          let endCyclePosition =
            (moment(dayItem.EndDate).diff(
              moment(dayItem.EndDate).format('YYYY-MM-DD 00:00:00'),
              'seconds'
            ) /
              this.dayDuration) *
              100 +
            '%'
          left = `calc(${endCyclePosition} - 24px)`
        }
        return left
      }
    },
    getEventInfo() {
      return event => {
        let info
        let type = event.Status
        switch (type) {
          case this.voidConst.small:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.small_void')
            }
            break
          case this.voidConst.medium:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/medium-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.medium_void')
            }
            break
          case this.voidConst.large:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/large-void.png'),
              class: 'void-event',
              text: event.VoidAmount
                ? `${event.VoidAmount} void`
                : this.$t('text.large_void')
            }
            break
          case this.voidConst.diaperNotSelected:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/diaper-not-selected.png'),
              class: 'diaper-not-selected',
              text: this.$t('text.diaper_not_selected', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.red:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/red-icon.png'),
              class: 'change-diaper',
              text: this.$t('text.change_diaper', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          // WETSURFACE, UNSTABLE, CLUMPED is a group, so these three states show the same icon, and group name is 'unstable readings'
          case this.voidConst.external:
          case this.voidConst.unstable:
          case this.voidConst.clumped:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/external-humidity.png'),
              class: 'external-humidity',
              text: this.$t('text.unstable_readings')
            }
            break
          case this.voidConst.outOfRange:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/out-of-range.png'),
              class: 'out-of-range',
              text: this.$t('text.out_of_range')
            }
            break
          case this.voidConst.padStart:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness1.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_start', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.padEndLevel1:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness1.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.padEndLevel2:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness2.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.padEndLevel3:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness3.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.padEndLevel4:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness4.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          case this.voidConst.padEndLevel5:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/pad-fullness5.png'),
              class: 'pad-fullness',
              text: this.$t('text.pad_end', {
                pad: this.$firstLetterCapitalize(this.$store.state.locale.pad)
              })
            }
            break
          default:
            info = {
              imgSrc: require('@/assets/image/VoidingPatternIcon/small-void.png'),
              class: 'void-event',
              text: this.$t('text.small_void')
            }
        }
        return info
      }
    },
    getBottomAndTopIcon() {
      // Make sure the RED icon are always on top when there are multiple voids staggered in the same place
      return item => {
        const { SubEvents } = item

        const allEvents = [item].concat(SubEvents)
        const allEventsStatus = allEvents.map(item => {
          return item.Status
        })

        if (!allEventsStatus.includes(voidConst.red)) {
          return {
            bottom: item,
            top: SubEvents.length > 0 ? SubEvents[SubEvents.length - 1] : null
          }
        }

        const firstRed =
          allEvents.find(event => event.Status === voidConst.red) || item
        const firstNonRed =
          allEvents.find(event => event.Status !== voidConst.red) || item

        // the diaper icon should have the priority
        const hasPadCycleEnd = allEventsStatus.some(item =>
          [
            this.voidConst.padEndLevel1,
            this.voidConst.padEndLevel2,
            this.voidConst.padEndLevel3,
            this.voidConst.padEndLevel4,
            this.voidConst.padEndLevel5
          ].includes(item)
        )
        if (hasPadCycleEnd) {
          return {
            bottom: firstRed,
            top: SubEvents.length > 0 ? SubEvents[SubEvents.length - 1] : null
          }
        }

        return {
          bottom: firstNonRed,
          top: firstRed
        }
      }
    }
  }
}
</script>
